import React from 'react';

import { useClients } from '@/clients/queries';
import { ListboxOption } from '@/common/components/listbox';
import type { useCreateUpdateProjectForm } from '@/projects/hooks/use-create-update-project-form';
import { PROJECT_BILLING_TYPE_OPTIONS } from '@/shared/projects';

type CreateUpdateProjectFormProps = {
  form: ReturnType<typeof useCreateUpdateProjectForm>;
  formId: string;
};

export function CreateUpdateProjectForm({ form, formId }: CreateUpdateProjectFormProps) {
  const clientsQuery = useClients();
  const clientLookups = clientsQuery.data?.map(client => ({
    id: client.id,
    name: client.name
  })) ?? [];

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();

    void form.handleSubmit();
  }

  return (
    <form id={formId} onSubmit={submit} className="gap-6 flex flex-col">
      {/* Client Selection */}
      <form.AppField
        name="client"
        children={field => (
          <field.FormListbox label="Client *" placeholder="Select a client">
            {clientLookups.length === 0 ?
                (
                  <ListboxOption value="" disabled>
                    No clients available
                  </ListboxOption>
                ) :
                (
                  clientLookups.map(client => (
                    <ListboxOption key={client.id} value={client}>
                      {client.name}
                    </ListboxOption>
                  ))
                )}
          </field.FormListbox>
        )}
      />

      {/* Project Details */}
      <form.AppField
        name="name"
        children={field => <field.FormInput label="Name *" />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.AppField
          name="budget"
          children={field => (
            <field.FormInput
              label="Budget"
              type="number"
              step="0.01"
            />
          )}
        />
        <form.AppField
          name="rate"
          children={field => (
            <field.FormInput
              label="Rate *"
              type="number"
              step="0.01"
            />
          )}
        />
      </div>

      {/* Billing Type */}
      <form.AppField
        name="billingType"
        children={field => (
          <field.FormListbox label="Billing Type *" placeholder="Select billing type">
            {PROJECT_BILLING_TYPE_OPTIONS.map(option => (
              <ListboxOption key={option.value} value={option.value}>
                {option.label}
              </ListboxOption>
            ))}
          </field.FormListbox>
        )}
      />

      {/* Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.AppField
          name="startOn"
          children={field => <field.FormInput label="Start Date" type="date" />}
        />
        <form.AppField
          name="endOn"
          children={field => <field.FormInput label="End Date" type="date" />}
        />
      </div>
    </form>
  );
}
